// Szolgáltatás-hozzárendelés create/edit — jobb oldali beúszó FormSheet (az asset-form-sheet mintája). Egy
// komponens KÉT módban: `assignment == null` → LÉTREHOZÁS (POST), különben SZERKESZTÉS (PUT If-Match/OCC).
// A validálás forrás-igazsága a @atlas/contracts ServiceAssignment{Create,Update}BodySchema (a resolver a
// features/service-assignments/form.ts séma-gyárán át fut).
//
// KASZKÁD (create): szolgáltatás → konstrukció (plan) — a refs `services`/`plans` (serviceId szerint szűrve); a
// kiválasztott plan adja a snapshotot + az ÁR-ELŐTÖLTÉST (a katalógus-árból). SZERKESZTÉS-LOCK: a detail `invoiced`
// + `billingType` szerint a UI DISABLE-öli a nem-szerkeszthető mezőket (a BFF is 422-zi), és a nem küldött mezőket
// kihagyja a body-ból. A strukturális mezők (ügyfél/fiók/szolgáltatás/plan/scope/típus) edit-nél ZÁROLTAK.
//
// Phase-2 blokkok: ÁR-OVERRIDE (közös PricingEditor + down-conversion korlát), payment-card, contact/expert pivot,
// copy-to-accounts (client-scope), invoiceItem* + showComponentsOnInvoice, komponens-fa (parentId-s gyerek-sorok).
import { type ServiceAssignmentRowSchema } from '@atlas/contracts';
import {
  INVOICE_DELIVERY_TYPES,
  INVOICE_ISSUER_KEYS,
  INVOICE_QUANTITY_UNITS,
  INVOICE_TYPES,
  canSplitSharedUsage,
} from '@atlas/shared';
import {
  BriefcaseIcon,
  BuildingOffice2Icon,
  EyeIcon,
  ReceiptPercentIcon,
} from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, type Resolver, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import {
  type ActiveAssignmentExisting,
  ServiceAssignmentActiveWarningDialog,
} from '@/components/service-assignments/service-assignment-active-warning-dialog';
import { AttentionBanner } from '@/components/shared/attention-banner';
import { type ComboboxOption } from '@/components/shared/entity-combobox';
import { EntityMultiSelect } from '@/components/shared/entity-multiselect';
import {
  ComboboxField,
  DateField,
  SegmentedField,
  SearchableSelectField,
  SelectField,
  SwitchField,
  TextField,
  TextareaField,
} from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { PricingEditor } from '@/components/shared/pricing-editor';
import { UsageRefsSelect } from '@/components/service-assignments/usage-refs-select';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { serviceAssignmentKeys } from '@/features/service-assignments/api';
import {
  allowedAssignmentPricingTypes,
  allowedStatuses,
  contactAccountScope,
  contactsForClient,
  buildServiceAssignmentErrorMap,
  buildServiceAssignmentSchema,
  catalogPricingToForm,
  collapseToMainPlan,
  componentPlanNames,
  componentsTotal,
  componentTargetBillingType,
  computeEditableFields,
  emptyComponent,
  EMPTY_SERVICE_ASSIGNMENT_FORM,
  mainPlanComponent,
  SERVICE_ASSIGNMENT_BILLING_AGREEMENTS,
  SERVICE_ASSIGNMENT_BILLING_FREQUENCIES,
  SERVICE_ASSIGNMENT_STATUSES,
  SERVICE_ASSIGNMENT_USAGE_SOURCE_TYPES,
  serviceAssignmentFormToCreateBody,
  serviceAssignmentFormToUpdateBody,
  serviceAssignmentToFormValues,
  splitAssignmentPeople,
  serviceMatchesComponentBillingType,
  type ServiceAssignmentFormValues,
  type UpdateField,
  isUsageBasedForm,
  usageRefOptions,
  usageSourceRequired,
} from '@/features/service-assignments/form';
import { api } from '@/lib/api';
import { CURRENCIES, VAT_RATES } from '@/lib/pricing-form';
import { useEntityMutation } from '@/lib/use-entity-mutation';

type ServiceAssignmentRow = z.infer<typeof ServiceAssignmentRowSchema>;

export interface ServiceAssignmentFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → LÉTREHOZÁS; egyébként a szerkesztendő sor (az id-jából töltjük a detailt + az OCC version-t). */
  assignment: ServiceAssignmentRow | null;
  /**
   * ELŐTÖLTÖTT, ZÁROLT ügyfél — akkor adjuk meg, ha a sheet egy KONKRÉT ügyfél detail-oldaláról nyílik.
   * CSAK az ÜGYFÉL zárol: a hatókör (ügyfél- vagy fiók-szintű) és a fiók-választó továbbra is nyitva marad,
   * mert azok a szülőn BELÜLI döntések — a fiók-lista amúgy is erre az ügyfélre szűrődik.
   */
  lockedClient?: { id: string; name: string } | undefined;
}

export function ServiceAssignmentFormSheet({
  open,
  onOpenChange,
  assignment,
  lockedClient,
}: ServiceAssignmentFormSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const isEdit = assignment !== null;

  // Form-forrás refs (katalógus + ügyfelek + fiókok + bankkártyák + identity-k) — jog-kapu service_assignments.manage.
  // A `lang` a személy-nevek SORRENDJÉT vezérli (hu: Vezetéknév Keresztnév · en: Given Family), és a
  // cache-kulcs része — nyelvváltáskor új lekérés kell, különben a régi sorrendű nevek maradnának.
  const lang = i18n.language.toLowerCase().startsWith('en') ? ('en' as const) : ('hu' as const);
  const refsQ = api.serviceAssignmentRefs.useQuery(
    serviceAssignmentKeys.refs(lang),
    { query: { lang } },
    { queryKey: serviceAssignmentKeys.refs(lang), enabled: open },
  );
  const refs = refsQ.data?.status === 200 ? refsQ.data.body : undefined;

  // Edit: a detail (a lock + az összes szerkeszthető mező + az OCC version forrása).
  const detailKey = serviceAssignmentKeys.detail(assignment?.id ?? '__none__');
  const detailQ = api.serviceAssignment.useQuery(
    detailKey,
    { params: { id: assignment?.id ?? '' } },
    { queryKey: detailKey, enabled: open && isEdit },
  );
  const detail = detailQ.data?.status === 200 ? detailQ.data.body : undefined;

  const editable: Set<UpdateField> = useMemo(
    () =>
      detail
        ? computeEditableFields(detail)
        : computeEditableFields({ invoiced: false, billingType: null }),
    [detail],
  );

  const form = useForm<ServiceAssignmentFormValues>({
    resolver: zodResolver(buildServiceAssignmentSchema(isEdit ? 'edit' : 'create', t), {
      errorMap: buildServiceAssignmentErrorMap(t),
    }) as Resolver<ServiceAssignmentFormValues>,
    defaultValues: EMPTY_SERVICE_ASSIGNMENT_FORM,
    // A hiba már MENTÉS ELŐTT látszik: blur-kor validál, utána gépelés közben frissül (reValidate: onChange).
    mode: 'onTouched',
  });
  const { reset, handleSubmit, setError, watch, setValue, control } = form;

  // A KASZKÁD-őrök: a hidratálás (reset) után beállított „utoljára ismert" service/plan — így a szolgáltatás→plan
  // és a plan→ár kaszkád CSAK a felhasználó VALÓS váltására fut le, a betöltésre NEM (különben edit-nél kitörölné a
  // betöltött plant/árat). Ez teszi lehetővé a plan-VÁLTÁST szerkesztéskor (nem-számlázott).
  /**
   * A HIDRATÁLÁS JELÖLŐJE — a betöltés UTÁNI első kör nem számít felhasználói műveletnek.
   *
   * ⚠️ MÉRT ÉLES HIBA: a komponens-megjegyzés újraszámoló effektje a betöltéskor is lefutott, és a TÁROLT
   * számlatétel-megjegyzést lecserélte a komponens-nevekből generáltra — `shouldDirty: true`-val, tehát a
   * form „piszkos" lett anélkül, hogy a felhasználó bármihez nyúlt volna, és egy mentés CSENDBEN elvitte a
   * tárolt szöveget. Mérve: a tárolt „… - sirius.adventorhotels.hu" helyett a generikus katalógus-szöveg.
   */
  /**
   * A KOMPONENS-MEGJEGYZÉS HORGONYA: a betöltés-kori (kapcsoló, komponens-nevek) pár — ehhez képest
   * nézzük, hogy a felhasználó TÉNYLEG változtatott-e. `null` = még nem hidratáltunk.
   *
   * MIÉRT A DETAILBŐL, nem a `watch`-ból: a `reset()` utáni ELSŐ renderben a `watch('components')` még a
   * korábbi (üres) értéket adhatja, és egy ilyen késésre épített horgony azt hinné, hogy a komponensek
   * „most jöttek létre" — vagyis egy puszta megnyitás írná felül a tárolt megjegyzést.
   */
  const komponensHorgony = useRef<{ kapcsolo: boolean; nevek: string } | null>(null);
  const prevServiceId = useRef<string>('');
  const prevPlanId = useRef<string>('');

  // Előtöltés: create → üres; edit → a detail (a serviceId-t a plan-katalógusból oldjuk fel).
  useEffect(() => {
    if (isEdit) {
      if (detail && refs) {
        const serviceId = refs.plans.find((p) => p.id === detail.servicePlanId)?.serviceId ?? '';
        reset(
          serviceAssignmentToFormValues(
            detail,
            serviceId,
            (planId) => refs.plans.find((p) => p.id === planId)?.serviceId ?? '',
          ),
        );
        prevServiceId.current = serviceId;
        prevPlanId.current = detail.servicePlanId;
        komponensHorgony.current = {
          kapcsolo: detail.showComponentsOnInvoice,
          nevek: componentPlanNames(detail.children, refs.plans),
        };
      }
    } else {
      // ZÁROLT ügyfél (a szülő detail-oldaláról nyitva): a form MÁR a szülővel indul.
      reset({ ...EMPTY_SERVICE_ASSIGNMENT_FORM, clientId: lockedClient?.id ?? '' });
      prevServiceId.current = '';
      prevPlanId.current = '';
      komponensHorgony.current = { kapcsolo: false, nevek: '' };
    }
  }, [isEdit, detail, refs, lockedClient, reset]);

  const scope = watch('scope');
  const clientId = watch('clientId');
  const accountId = watch('accountId');
  const status = watch('status');
  const serviceId = watch('serviceId');
  const servicePlanId = watch('servicePlanId');
  const type = watch('type');
  // A `watch()` a form-REGISZTRÁCIÓ ELŐTT `undefined`-ot ad (lásd a plan-editor ugyanezen sorát): a közös
  // árazó-blokk ebből dinamikus i18n-kulcsot fűz, és a felületen nyers kulcs jelent meg címke helyett.
  const pricingType = watch('pricing.pricingType') ?? 'flat';
  // A SOR SAJÁT áfakulcsa (a felülírás) — a katalógus-összegző ettől eltérhet, és akkor azt KI IS MONDJA.
  const vatRate = watch('pricing.vatRate');
  const catalogPricingType = watch('catalogPricingType');
  // ESETI DÍJ (a katalógus-plan díjtípusa): egyetlen „Számlázás dátuma" — szerződés-dátum nincs (user-döntés)
  const isOneTime = watch('billingType') === 'one_time';

  // KOMPONENS-FA (komplex/csomag) — a gyerekek dinamikus listája (RHF field-array).
  const {
    fields: componentFields,
    append: appendComponent,
    remove: removeComponentAt,
  } = useFieldArray({ control, name: 'components' });
  const componentsWatch = watch('components');
  const componentsSummary = componentsTotal(componentsWatch ?? []);
  const hasComponents = componentFields.length > 0;

  /**
   * „Komponens hozzáadása" (user-döntés, a legacy CRM működése): komponens módban a szülő ára a komponensek
   * összege, ezért az ELSŐ kattintás a FŐ csomagot is komponensnek teszi a már beírt árral — az alapdíj így sosem
   * veszhet el —, alatta az új, üres sor. Ha a fő csomag már komponens (pl. migrált sor), csak az üres sor jön.
   */
  const addComponent = (): void => {
    const v = form.getValues();
    const mainAlready = v.components.some((c) => c.servicePlanId === v.servicePlanId);
    if (v.components.length === 0 && v.servicePlanId !== '' && !mainAlready) {
      appendComponent([mainPlanComponent(v), emptyComponent()]);
    } else {
      appendComponent(emptyComponent());
    }
  };

  /**
   * Komponens törlése. Ha utána CSAK a fő csomag marad, a sor visszaáll EGYSZERŰ szolgáltatássá azzal az árral és
   * mennyiséggel (nem marad egy egy-komponenses „komplex" sor).
   */
  const removeComponent = (index: number): void => {
    removeComponentAt(index);
    const v = form.getValues();
    const back = collapseToMainPlan(v.components, v.servicePlanId);
    if (!back) return;
    removeComponentAt(0);
    setValue('pricing.pricingType', 'flat', { shouldDirty: true });
    setValue('pricing.primaryPrice', back.unitPrice, { shouldDirty: true });
    setValue('pricing.primaryCurrency', back.currency, { shouldDirty: true });
    setValue('quantity', back.quantity, { shouldDirty: true });
  };

  // ── ÜGYFÉL-KASZKÁD (CSAK create — edit-nél a tulajdonos zárolt) ──
  //
  // Ügyfél-VÁLTÁSKOR az ügyfélhez kötött választások elévülnek: a fiók, a bankkártya és a KAPCSOLATTARTÓ is
  // (ez utóbbi az ügyfél fiókjaihoz kötött partner-oldali személy — a másik ügyfélnél értelmezhetetlen, és a
  // szerver NEM ellenőrzi, tehát itt a fal). Az ÉRINTETT FIÓKOK listája pedig az ÚJ ügyfél ÖSSZES fiókjára áll:
  // a legacy pontosan ezt tette (`updatedFormClientId` → `pluck('id')`), és nem kényelmi lépés — az üres
  // kijelölés a generálóban „az ügyfél összes fiókja" jelentésű fallback, tehát az üres mező CSENDBEN ugyanazt
  // jelentené, de a szerződésben rögzítetlenül.
  //
  // A `refs`-et MEG KELL VÁRNI (a fiók-lista onnan jön), ezért az őr nem a hatás-lista, hanem egy ref: a
  // hatás a refs megérkezésekor fut le először, és utána CSAK valódi ügyfél-váltásra. Hidratálás nincs itt
  // (create-mód), de az elv ugyanaz: a betöltés nem felhasználói művelet.
  const elozoUgyfel = useRef<string | null>(null);
  useEffect(() => {
    if (isEdit || !refs) return;
    const valtott = elozoUgyfel.current !== null && elozoUgyfel.current !== clientId;
    if (elozoUgyfel.current === clientId) return;
    elozoUgyfel.current = clientId;
    if (valtott) {
      setValue('accountId', '');
      setValue('paymentCardId', '');
      setValue('contactId', '');
    }
    setValue(
      'accountIds',
      refs.accounts.filter((a) => a.clientId === clientId).map((a) => a.id),
    );
  }, [clientId, isEdit, refs, setValue]);

  // HATÓKÖR-VÁLTÁS fiókról ügyfélre: ekkor jelenik meg az „Érintett fiókok" mező — töltsük fel az ügyfél
  // minden fiókjával (a legacy `updatedFormScope` 1:1). CSAK a VALÓDI váltásra fut, hogy egy szándékos
  // „mindet leveszem" ne íródjon vissza azonnal.
  const elozoScope = useRef<string | null>(null);
  useEffect(() => {
    if (isEdit || !refs) return;
    const valtott = elozoScope.current !== null && elozoScope.current !== scope;
    elozoScope.current = scope;
    if (!valtott || scope !== 'client' || clientId === '') return;
    setValue(
      'accountIds',
      refs.accounts.filter((a) => a.clientId === clientId).map((a) => a.id),
    );
  }, [scope, clientId, isEdit, refs, setValue]);

  // Szolgáltatás→plan kaszkád (create ÉS nem-számlázott edit): VALÓS szolgáltatás-váltáskor a plan-választó ürül.
  // A hidratálás nem triggereli (prevServiceId a betöltött értékre van állítva).
  useEffect(() => {
    if (serviceId === prevServiceId.current) return;
    prevServiceId.current = serviceId;
    setValue('servicePlanId', '');
  }, [serviceId, setValue]);

  // Ár-ELŐTÖLTÉS (create ÉS nem-számlázott edit plan-VÁLTÁS): a KATALÓGUS-plan árából + a snapshot invoiceItem*/
  // usageSourceType-ból; one_time plan → type=tracking. A hidratálás nem triggereli (prevPlanId a betöltött plan).
  useEffect(() => {
    if (!refs) return; // várd meg a refs-et (a prev-t nem frissítjük → refs betöltésekor újra fut)
    if (servicePlanId === prevPlanId.current) return;
    // ⚠️ AZ ÜRES ÉRTÉKRE KILÉPÜNK, MIELŐTT A `prev`-ET ÁTÍRNÁNK — a sorrend itt load-bearing.
    //
    // MÉRT ÉLES HIBA: a hidratálás egyetlen függvényben hív `reset(...)`-et ÉS állítja a `prev`-eket, de a
    // `watch`-olt `servicePlanId` csak a KÖVETKEZŐ renderben veszi fel az új értéket. Ezért ugyanabban a
    // commitban ez a kaszkád még a RÉGI (üres) plant látja. Ha ilyenkor átírja a `prev`-et üresre, akkor a
    // következő renderben — amikor a `servicePlanId` már a hidratált plan — az őr „változásnak" látja, lefut,
    // és FELÜLÍRJA a hozzárendelés saját értékeit a KATALÓGUS-plan értékeivel: az árat, a számlatétel-nevet és
    // a HASZNÁLAT FORRÁSÁT (ami a katalóguson jellemzően üres). A felhasználó ebből annyit lát, hogy a
    // szerkesztő „nem tölti be" a forrást — másodszorra viszont már jó, mert akkor más a timing.
    //
    // Üresre kilépve a `prev` a hidratált planon MARAD, tehát a következő render nem látszik változásnak.
    // A VALÓDI plan-váltást ez nem érinti: ott a `prev` a régi plan, az új plan pedig eltér tőle.
    if (servicePlanId === '') return;
    prevPlanId.current = servicePlanId;
    const plan = refs.plans.find((p) => p.id === servicePlanId);
    if (!plan) return;
    const planPrices = refs.planPrices.filter((pp) => pp.planId === servicePlanId);
    const { pricing, catalogPricingType: catType } = catalogPricingToForm(plan, planPrices);
    setValue('pricing', pricing);
    setValue('catalogPricingType', catType);
    setValue('invoiceItemName', plan.invoiceItemName ?? '');
    setValue('invoiceItemNote', plan.invoiceItemNote ?? '');
    setValue('usageSourceType', plan.usageSourceType ?? '');
    // a díjtípus a dátum-mezők kapuja (eseti díjnál egyetlen „Számlázás dátuma"). ESETI csomagról váltva a
    // régi záró dátum (= a számlázás napja) maradvány — ürül, különben az új ismétlődő sor lezártan születne.
    if (form.getValues('billingType') === 'one_time' && plan.billingType !== 'one_time') {
      setValue('billingEndDate', '');
    }
    setValue('billingType', plan.billingType ?? '');
    // one_time konstrukció → a hozzárendelés kizárólag tracking lehet (a legacy 1:1).
    if (plan.billingType === 'one_time') setValue('type', 'tracking');
    // KOMPONENS-billing-type reconcile (GAP-2): a fő plan VÁLTÁSAKOR a már felvett, nem-egyező billing-type-ú
    // komponenseket eldobjuk (a legacy component-modal a választót szűri; itt a plan-váltás dependens-állapotot
    // reset-el — a pricing/type-hoz hasonlóan). A még ki nem választott (üres serviceId) sorokat MEGTARTJUK.
    const target = componentTargetBillingType(plan.billingType);
    const current = form.getValues('components');
    const kept = current.filter(
      (c) =>
        c.serviceId === '' || serviceMatchesComponentBillingType(c.serviceId, refs.plans, target),
    );
    if (kept.length !== current.length) setValue('components', kept);
  }, [servicePlanId, refs, setValue, form]);

  // ── „KOMPONENSEK A SZÁMLATÉTELEN" (legacy §9.5) ──
  // A kapcsoló BEKAPCSOLVA a számlatétel-megjegyzést a komponens-csomagok NEVEIBŐL állítja elő (vesszővel),
  // és minden komponens-változásnál újraszámolja; KIKAPCSOLVA törli (de CSAK a saját szövegét). A legacy
  // pontosan ezt tette a formon (`updateInvoiceNoteFromComponents`), és a generálásnak nem volt külön
  // logikája — a TÁROLT megjegyzés megy a számlára. Korábban a kapcsoló HALOTT volt (tárolódott, de semmi
  // nem használta); a bekötés után pedig a hidratálás-jelző nyelte el az első kattintást — lásd lent.
  const showComponentsOnInvoice = watch('showComponentsOnInvoice');
  const componentNames = useMemo(
    () => componentPlanNames(componentsWatch ?? [], refs?.plans ?? []),
    [componentsWatch, refs],
  );
  useEffect(() => {
    const horgony = komponensHorgony.current;
    // (1) Amíg nem hidratáltunk (edit, de még nincs detail), nem nyúlunk semmihez.
    if (horgony === null) return;
    const kapcsolo = showComponentsOnInvoice === true;
    // (2) ÁTMENETI ÜRES: a `reset()` utáni első renderben a `watch('components')` még késhet, és a nevek
    //     üresek, pedig a soron VANNAK komponensek. Ez nem változás — különben egy puszta megnyitás
    //     törölné/újraírná a tárolt megjegyzést.
    if (componentNames === '' && horgony.nevek !== '') return;
    // (3) NINCS VÁLTOZÁS a betöltés-kori állapothoz képest → a tárolt megjegyzés a felhasználó döntése
    //     volt (gyakran kézzel pontosított, pl. domain-utótaggal), és egy megnyitás nem írhatja felül.
    if (kapcsolo === horgony.kapcsolo && componentNames === horgony.nevek) return;
    komponensHorgony.current = { kapcsolo, nevek: componentNames };
    // (4) KIKAPCSOLÁS: CSAK az általunk generált szöveget töröljük. A bizonyíték maga a szöveg — ha a mező
    //     bitre a komponens-nevekkel egyezik, akkor mi írtuk; bármi más a felhasználóé, és marad.
    if (!kapcsolo) {
      if (componentNames !== '' && form.getValues('invoiceItemNote') === componentNames) {
        setValue('invoiceItemNote', '', { shouldDirty: true });
      }
      return;
    }
    // (5) BEKAPCSOLVA: a megjegyzés a komponens-csomagok nevei, és minden komponens-változásra frissül.
    if (componentNames === '') return;
    if (form.getValues('invoiceItemNote') === componentNames) return;
    setValue('invoiceItemNote', componentNames, { shouldDirty: true });
  }, [showComponentsOnInvoice, componentNames, form, setValue]);

  // A tulajdonos (ügyfél/fiók) státusza — a create-form státusz-szűkítés forrása (edit-nél nincs korlát).
  const ownerStatus: string | null = useMemo(() => {
    if (isEdit || !refs) return null;
    if (scope === 'account') {
      return accountId === ''
        ? null
        : (refs.accounts.find((a) => a.id === accountId)?.status ?? null);
    }
    return clientId === '' ? null : (refs.clients.find((c) => c.id === clientId)?.status ?? null);
  }, [isEdit, refs, scope, accountId, clientId]);

  // Owner-státusz auto-választás (CSAK create; a legacy setDefaultStatusBasedOnOwner 1:1): ha az aktuális státusz
  // nem engedélyezett, a LEGMAGASABB engedélyezettre vált (az allowed[0]).
  useEffect(() => {
    if (isEdit) return;
    const allowed = allowedStatuses(ownerStatus);
    if (!allowed.includes(status)) setValue('status', allowed[0]!);
  }, [ownerStatus, status, isEdit, setValue]);

  // DÍJBEKÉRŐ → papír alapú: a díjbekérő csak papír alapú lehet, ezért a választó eltűnik, és az érték
  // papírra áll (a felhasználó nem hagyhat ott egy e-számla értéket, ami a díjbekérőre értelmetlen).
  const invoiceType = watch('invoiceType');
  useEffect(() => {
    if (invoiceType === 'proforma' && form.getValues('invoiceDeliveryType') !== 'paper') {
      setValue('invoiceDeliveryType', 'paper', { shouldDirty: true });
    }
  }, [invoiceType, form, setValue]);

  const mutation = useEntityMutation<ServiceAssignmentFormValues>({
    mutationFn: (values) =>
      isEdit
        ? api.serviceAssignmentUpdate.mutation({
            params: { id: assignment.id },
            // az árazáshoz hozzányúltak-e — a NYILVÁNTARTÁS-soron csak ilyenkor megy az ár (l. a body-építőt)
            body: serviceAssignmentFormToUpdateBody(values, editable, {
              pricingDirty: Boolean(form.formState.dirtyFields.pricing),
            }),
            headers: { 'if-match': String(detail?.version ?? assignment.version) },
          })
        : api.serviceAssignmentCreate.mutation({
            body: serviceAssignmentFormToCreateBody(values),
          }),
    invalidateKeys: [serviceAssignmentKeys.lists()],
    onFieldErrors: (field, messages) => {
      if (field in EMPTY_SERVICE_ASSIGNMENT_FORM) {
        setError(field as keyof ServiceAssignmentFormValues, { message: messages.join(' ') });
      }
    },
    messages: {
      success: isEdit
        ? t('toast:serviceAssignments.updated')
        : t('toast:serviceAssignments.created'),
      conflict: t('toast:serviceAssignments.conflict'),
      forbidden: t('toast:serviceAssignments.forbidden'),
      validation: t('toast:serviceAssignments.validation'),
      error: t('toast:serviceAssignments.error'),
    },
  });

  // Aktív-hozzárendelés FIGYELMEZTETÉS (nem-blokkoló) állapota: a mentés ELŐTT lekért ellenőrzés eredménye.
  const [warnOpen, setWarnOpen] = useState(false);
  const [warnExisting, setWarnExisting] = useState<ActiveAssignmentExisting | null>(null);
  const [pendingValues, setPendingValues] = useState<ServiceAssignmentFormValues | null>(null);
  const [checking, setChecking] = useState(false);

  const doSubmit = (values: ServiceAssignmentFormValues): void => {
    mutation.mutate(values, { onSuccess: () => onOpenChange(false) });
  };

  const onSubmit = handleSubmit(async (values) => {
    // Aktív-hozzárendelés ellenőrzés (a legacy checkForActiveAssignment 1:1): create MINDIG; edit CSAK ha a
    // szolgáltatás változott (a tulajdonos edit-nél zárolt). one_time → a BFF kihagyja (active:false). NEM blokkol:
    // a check hibája esetén is mentünk (best-effort figyelmeztetés).
    const plan = refs?.plans.find((p) => p.id === values.servicePlanId);
    const originalServiceId = isEdit
      ? (refs?.plans.find((p) => p.id === detail?.servicePlanId)?.serviceId ?? '')
      : '';
    const serviceChanged = isEdit ? values.serviceId !== originalServiceId : true;
    // A KOMPONENSEK is ellenőrzés alá esnek (a legacy `collectServiceIdsToCheck`: fő szolgáltatás + a
    // komponensek szolgáltatásai), ezért szerkesztésnél a komponens-készlet változása is indokolja az
    // ellenőrzést — különben egy ÚJ komponens felvétele változatlan fő szolgáltatás mellett néma maradna.
    const componentPlanIds = (values.components ?? [])
      .map((c) => c.servicePlanId)
      .filter((id): id is string => typeof id === 'string' && id !== '');
    const originalComponentPlanIds = (detail?.children ?? []).map((c) => c.servicePlanId);
    const componentsChanged =
      componentPlanIds.length !== originalComponentPlanIds.length ||
      componentPlanIds.some((id) => !originalComponentPlanIds.includes(id));
    if (plan?.billingType !== 'one_time' && (serviceChanged || (isEdit && componentsChanged))) {
      try {
        setChecking(true);
        const res = await api.serviceAssignmentActiveCheck.query({
          query: {
            servicePlanId: values.servicePlanId,
            clientId: values.clientId,
            accountId: values.scope === 'account' ? values.accountId : undefined,
            scope: values.scope,
            excludeId: assignment?.id,
            ...(componentPlanIds.length > 0 ? { componentServicePlanIds: componentPlanIds } : {}),
          },
        });
        if (res.status === 200 && res.body.active) {
          setWarnExisting(res.body.existing);
          setPendingValues(values);
          setWarnOpen(true);
          return;
        }
      } catch {
        // a check hibája NEM blokkolja a mentést
      } finally {
        setChecking(false);
      }
    }
    doSubmit(values);
  });

  // ── Opció-listák ──
  const clientOptions = (refs?.clients ?? []).map((c) => ({ value: c.id, label: c.name }));
  const accountsForClient = (refs?.accounts ?? []).filter((a) => a.clientId === clientId);
  const accountOptions = accountsForClient.map((a) => ({ value: a.id, label: a.name }));
  const serviceOptions = (refs?.services ?? []).map((s) => ({ value: s.id, label: s.name }));
  // A CSOMAG-OPCIÓ FELIRATA EMBERI SZÖVEG, nem gépi kulcs: az árazási modell és az áfa-kulcs a saját
  // i18n-szótárából jön (`flat` → „Fix díjas", `VAT_27` → „27% ÁFA"). Korábban a nyers enum-érték állt a
  // legördülőben — „Marketing audit · flat · VAT_27" —, ami a katalógus belső nyelvét szivárogtatta ki.
  const planOptions = (refs?.plans ?? [])
    .filter((p) => p.serviceId === serviceId)
    .map((p) => ({
      value: p.id,
      label: [
        p.name,
        p.pricingType
          ? t(`serviceAssignments.pricingTypeLabel.${p.pricingType}`, {
              defaultValue: p.pricingType,
            })
          : null,
        p.vatRate
          ? t(`serviceAssignments.vatRateLabel.${p.vatRate}`, { defaultValue: p.vatRate })
          : null,
      ]
        .filter(Boolean)
        .join(' · '),
    }));
  const selectedPlan = (refs?.plans ?? []).find((p) => p.id === servicePlanId);

  // KOMPONENS billing-type-szűrő (GAP-2, a legacy component-modal 1:1): a komponens-választó a FŐ plan billing-
  // type-jához igazodik. one_time fő plan → CSAK one_time komponens; recurring (vagy még nincs fő plan) → recurring.
  const componentBillingTarget = componentTargetBillingType(selectedPlan?.billingType);
  // A FŐ szolgáltatás és a FŐ CSOMAG is választható komponensnek (a legacy CRM így működött): komponens módban a
  // szülő ára a komponensek összege, tehát az alapdíj csak úgy marad meg, ha a fő csomag is komponens. Kétszeres
  // számlázás nincs: a szülőnek ilyenkor nincs önálló ára (Σ), a komponensek sosem külön számlatételek.
  const componentServiceOptions = (refs?.services ?? [])
    .filter((s) =>
      serviceMatchesComponentBillingType(s.id, refs?.plans ?? [], componentBillingTarget),
    )
    .map((s) => ({ value: s.id, label: s.name }));

  // Payment-card / kapcsolattartó / szakértő / copy-to-accounts opciók.
  const paymentCardId = watch('paymentCardId');
  const paymentCardOptions = [
    { value: '', label: t('serviceAssignments.form.paymentCardNone') },
    ...(refs?.paymentCards ?? [])
      .filter((pc) => pc.clientId === clientId)
      .map((pc) => ({
        value: pc.id,
        label: `${pc.holderName} · ${pc.brand.toUpperCase()} · •••• ${pc.last4}`,
      })),
  ];
  // ⚠️ A BETÖLTÖTT ÉRTÉK MINDIG SZEREPEL A LISTÁBAN. A kártya-lista a `refs`-ből jön, az viszont KÉSŐBB
  // érkezik, mint a hidratálás — addig a lista csak a „nincs"-et tartalmazza. A Radix rejtett natív
  // `<select>`-je ilyenkor az első opcióra esne vissza, change-et sütne el, és a mező némán kiürülne. Ezt a
  // mezőt az `''`-őr NEM védi meg (üres érték LEGITIM választás rajta, l. `form-fields.tsx`), ezért a
  // védelem az, hogy a jelenlegi érték sosem hiányzik a listából. A címke a refs megérkezésekor a valódira
  // cserélődik.
  if (paymentCardId !== '' && !paymentCardOptions.some((o) => o.value === paymentCardId)) {
    paymentCardOptions.push({ value: paymentCardId, label: t('common.loading') });
  }

  // KAPCSOLATTARTÓ vs. SZAKÉRTŐ — KÉT KÜLÖN halmaz, nem ugyanaz a lista kétszer:
  //   • szakértő      = a cég BELSŐ munkatársa (`internal`),
  //   • kapcsolattartó = az ügyfél-oldali KÜLSŐS (`partner`).
  // Egy közös listából a felhasználó bármikor a rossz oldalról választhatna, és ezt semmi nem jelezné
  // (adat-szinten a pivot mindkettőt elfogadja). A `service` típusú gépi fiókok EGYIKBEN sem jelennek meg.
  const { contacts: mindenKapcsolattarto, experts: expertOptions } = splitAssignmentPeople(
    refs?.identities ?? [],
  );
  // A kapcsolattartó-választó a HOZZÁRENDELÉS ÁLTAL ÉRINTETT fiókok kapcsolattartóira szűkül: fiók-hatókörnél
  // a kiválasztott fiókéira, ügyfél-hatókörnél a KIJELÖLT érintett fiókokéira (kijelölés nélkül az ügyfél
  // összes fiókjára) — a legacy `loadAvailableContacts` 1:1, l. `contactAccountScope`.
  // A SZAKÉRTŐ-lista nem szűkül: az a cég belső munkatársa, bármelyikük lehet felelős bármelyik ügyfélnél.
  const contactOptions = contactsForClient(
    mindenKapcsolattarto,
    refs?.contactAffiliations ?? [],
    contactAccountScope(
      scope,
      accountId,
      watch('accountIds') ?? [],
      accountsForClient.map((a) => a.id),
    ),
    [watch('contactId'), ...(componentsWatch ?? []).map((c) => c.contactId ?? '')],
  );
  // A lista már NÉVSOR szerint jön a szervertől (a látott néven rendezve) — itt csak szűrünk.
  // A keresés lokális: a refs teljes egészében betöltött, tehát a találat AZONNAL frissül (nincs kör a hálózaton).
  const searchIn =
    (options: ComboboxOption[]) =>
    (q: string): Promise<ComboboxOption[]> => {
      const needle = q.trim().toLowerCase();
      return Promise.resolve(
        needle === '' ? options : options.filter((o) => o.label.toLowerCase().includes(needle)),
      );
    };
  /** Edit-előtöltés: a mentett id-hoz tartozó címke (különben üresen nyílna a választó). */
  const optionById = (options: ComboboxOption[], id: string): ComboboxOption | null =>
    options.find((o) => o.id === id) ?? null;
  const accountMultiOptions = accountsForClient.map((a) => ({ id: a.id, name: a.name }));

  // Create-nél a státusz-opciók a tulajdonos állapota szerint szűkülnek (a tiltottak disabled); edit-nél mind.
  const allowedStatusSet = new Set(allowedStatuses(ownerStatus));
  const statusOptions = SERVICE_ASSIGNMENT_STATUSES.map((val) => ({
    value: val,
    label: t(`serviceAssignments.statusLabel.${val}`, { defaultValue: val }),
    disabled: !isEdit && !allowedStatusSet.has(val),
  }));
  const unitOptions = INVOICE_QUANTITY_UNITS.map((val) => ({
    value: val,
    label: t(`serviceAssignments.quantityUnitLabel.${val}`, { defaultValue: val }),
  }));
  const issuerOptions = INVOICE_ISSUER_KEYS.map((val) => ({
    value: val,
    label: t(`serviceAssignments.issuerLabel.${val}`, { defaultValue: val }),
  }));
  const invoiceTypeOptions = INVOICE_TYPES.map((val) => ({
    value: val,
    label: t(`serviceAssignments.invoiceTypeLabel.${val}`, { defaultValue: val }),
  }));
  const deliveryOptions = INVOICE_DELIVERY_TYPES.map((val) => ({
    value: val,
    label: t(`serviceAssignments.deliveryTypeLabel.${val}`, { defaultValue: val }),
  }));
  const frequencyOptions = [
    { value: '', label: t('serviceAssignments.form.frequencyNone') },
    ...SERVICE_ASSIGNMENT_BILLING_FREQUENCIES.map((val) => ({
      value: val,
      label: t(`serviceAssignments.frequencyLabel.${val}`, { defaultValue: val }),
    })),
  ];
  const agreementOptions = [
    { value: '', label: t('serviceAssignments.form.agreementNone') },
    ...SERVICE_ASSIGNMENT_BILLING_AGREEMENTS.map((val) => ({
      value: val,
      label: t(`serviceAssignments.agreementLabel.${val}`, { defaultValue: val }),
    })),
  ];
  // Ár-override: a pricingType választó a down-conversion korláttal (katalógus VAGY flat).
  //
  // ⚠️ A JELENLEGI ÉRTÉK MINDIG BENNE VAN, akkor is, ha a down-conversion szabály ma nem engedné választani.
  // MIÉRT: a lista a `catalogPricingType`-ból áll elő, az viszont a hidratálással EGYÜTT áll be — van olyan
  // render, amikor a mező értéke már a tárolt `base_tiered`, a lista pedig még csak `['flat']`. Egy
  // vezérelt Radix Select ilyenkor ÖN-TÖRLÉST végez (l. `SelectField`), a felhasználó pedig üres mezőt lát.
  // Attól függetlenül is helyes: a form MUTASSA, ami TÁROLVA van — ha egy régi sor olyan árazást hordoz,
  // amit ma már nem lehetne beállítani, azt látni kell, nem elrejteni.
  const pricingTypeOptions = Array.from(
    new Set<string>([...allowedAssignmentPricingTypes(catalogPricingType), pricingType]),
  ).map((val) => ({
    value: val,
    label: t(`serviceAssignments.pricingTypeLabel.${val}`, { defaultValue: val }),
  }));
  const vatRateOptions = VAT_RATES.map((val) => ({
    value: val,
    label: t(`serviceAssignments.vatRateLabel.${val}`, { defaultValue: val }),
  }));
  // KOMPONENS-fa deviza-választó (a közös pénznem-lista).
  const currencyOptions = CURRENCIES.map((val) => ({ value: val, label: val }));

  // HATÓKÖR és TÍPUS: SZEGMENTÁLT választó (mint az üzenet-írás típus/címzés választója) — user-döntés,.
  // Miért nem `PillField`: a telített zöld aktív pill az „elsődleges akció" hangsúlyát vitte egy
  // SEMLEGES dimenzióra (a hatókör és a típus nem státusz). Az ikonok a listával egyeznek: ügyfél = aktatáska,
  // fiók = épület, számlázás = nyugta, nyilvántartás = szem.
  const scopeOptions = [
    {
      value: 'client',
      label: t('serviceAssignments.scope.client'),
      icon: <BriefcaseIcon className="size-3.5" />,
    },
    {
      value: 'account',
      label: t('serviceAssignments.scope.account'),
      icon: <BuildingOffice2Icon className="size-3.5" />,
    },
  ];
  const typeOptions = [
    {
      value: 'tracking',
      label: t('serviceAssignments.typeLabel.tracking'),
      icon: <EyeIcon className="size-3.5" />,
    },
    {
      value: 'billing',
      label: t('serviceAssignments.typeLabel.billing'),
      icon: <ReceiptPercentIcon className="size-3.5" />,
    },
  ];

  const isBilling = type === 'billing';
  // Egy locked mező disabled-e (edit + a szerkeszthető halmaz nem tartalmazza). Create-nél a halmaz teljes → false.
  const dis = (field: UpdateField): boolean => isEdit && !editable.has(field);
  // Ár-blokk szerkeszthető-e (create VAGY nem-számlázott edit) — a lock a `pricing` update-mezőn. Ez EGYBEN a
  // szolgáltatás/plan-VÁLTÁS engedélye is: nem-számlázottnál a service/plan-választó él, számlázottnál zárolt.
  const pricingEditable = !isEdit || editable.has('pricing');

  // usageSourceType — a mező LÁTHATÓ/kötelező, ha az EFFEKTÍV árazás-típus tiered/base_tiered/commission.
  // Az EFFEKTÍV típus MINDKÉT hozzárendelés-típusnál az override: a nyilvántartás-sor árazása is a felhasználó
  // döntése. (Korábban tracking-nél a KATALÓGUS típusa döntött, mert a sor egyáltalán nem kapott ár-sort.)
  // A legacy 1:1 (a BFF is 422-zi a hiányt).
  const effectivePricingType = pricingType;
  const showUsageSource = usageSourceRequired(effectivePricingType);
  const usageSourceOptions = [
    { value: '', label: t('serviceAssignments.form.usageSourceNone') },
    ...SERVICE_ASSIGNMENT_USAGE_SOURCE_TYPES.map((val) => ({
      value: val,
      label: t(`usageData.sourceType.${val}`, { defaultValue: val }),
    })),
  ];

  // A MÉRT AZONOSÍTÓK — használat-alapú tételnél kötelező választás: melyik fiók melyik azonosítójának mérése adja
  // az összegét. Az opciók a fiók(ok) adatlapján felvett, a forráshoz illő rendszerű azonosítók.
  const usageSourceType = watch('usageSourceType') ?? '';
  const watchedAccountIds = watch('accountIds');
  const showUsageRefs = isUsageBasedForm({
    pricing: { pricingType: effectivePricingType },
    usageSourceType,
    components: componentsWatch ?? [],
  });
  const refOptions = useMemo(
    () =>
      usageRefOptions({
        scope,
        accountId,
        accountIds: watchedAccountIds ?? [],
        usageSourceType,
        accounts: refs?.accounts ?? [],
      }),
    [scope, accountId, watchedAccountIds, usageSourceType, refs],
  );
  // A már NEM választható kötések eldobása (fiók-/hatókör-/forrás-váltás, vagy a fiókról időközben törölt
  // azonosító) — CSAK betöltött refs mellett. A kötés így sosem mutathat olyanra, amit a felület nem mutat (a
  // BFF 422-je is ezt fogná meg).
  //
  // ⚠️ AZ OPCIÓKAT ITT A FORM AKTUÁLIS ÁLLAPOTÁBÓL számoljuk (`getValues`), nem a render-kori `watch`-ból. A
  // hidratálás ugyanabban a commitban hív `reset`-et, amelyben a refs megérkezik — a render-kori opciók ilyenkor
  // még az ÜRES űrlapéi, és azokkal mérve a frissen betöltött kötés „érvénytelennek" látszana (mérve: a tárolt
  // kötés a megnyitáskor eltűnt). A `refOptionKeys` csak a TRIGGER.
  const refOptionKeys = refOptions.map((o) => o.key).join(',');
  useEffect(() => {
    if (!refs) return;
    const v = form.getValues();
    const valid = new Set(
      usageRefOptions({
        scope: v.scope,
        accountId: v.accountId,
        accountIds: v.accountIds ?? [],
        usageSourceType: v.usageSourceType ?? '',
        accounts: refs.accounts,
      }).map((o) => o.key),
    );
    const current = v.usageRefs ?? [];
    const kept = current.filter((k) => valid.has(k));
    if (kept.length !== current.length) setValue('usageRefs', kept, { shouldDirty: true });
  }, [refOptionKeys, refs, form, setValue]);

  // Read-only kijelző-blokk (edit-nél a strukturális mezők zároltak).
  // a `hint` a szerkeszthető mezők súgójának alakja (`form-fields.tsx`) — a zárolt mezőnél azt mondja meg, MIÉRT zárolt
  const readonlyRow = (
    label: string,
    value: string | null | undefined,
    hint?: string,
  ): JSX.Element => (
    <div>
      <p className="text-sm font-medium">{label}</p>
      <p className="text-muted-foreground mt-1 text-sm">{value ?? '—'}</p>
      {hint && <p className="text-subtle mt-1 text-xs">{hint}</p>}
    </div>
  );

  return (
    <>
      <FormSheet
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            reset({ ...EMPTY_SERVICE_ASSIGNMENT_FORM, clientId: lockedClient?.id ?? '' });
            setWarnOpen(false);
            setPendingValues(null);
            setWarnExisting(null);
          }
          onOpenChange(o);
        }}
        title={isEdit ? t('serviceAssignments.editTitle') : t('serviceAssignments.createTitle')}
        description={
          isEdit ? (assignment.serviceName ?? undefined) : t('serviceAssignments.createDesc')
        }
        onSubmit={onSubmit}
        busy={mutation.isPending || checking}
        footer={
          <>
            <Button
              variant="outline"
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending || checking}
            >
              {t('serviceAssignments.cancel')}
            </Button>
            <Button
              type="submit"
              variant="brand"
              disabled={mutation.isPending || checking || (isEdit && !detail)}
            >
              {mutation.isPending || checking
                ? t('serviceAssignments.saving')
                : t('serviceAssignments.save')}
            </Button>
          </>
        }
      >
        <Form {...form}>
          {/* Szerkesztés-lock jelzés (számlázott assignment) — a KÖZÖS `AttentionBanner` (2. hívó, promóció). */}
          {isEdit && detail?.invoiced && (
            <AttentionBanner
              tone="warning"
              title={
                detail.billingType === 'recurring'
                  ? t('serviceAssignments.lockedRecurring')
                  : t('serviceAssignments.lockedOneTime')
              }
            />
          )}

          {/* 1. Címzett (scope + ügyfél + fiók) — edit-nél zárolt. */}
          {isEdit ? (
            <div className="space-y-3">
              {readonlyRow(t('serviceAssignments.form.client'), assignment.clientName)}
              {assignment.scope === 'account' &&
                readonlyRow(t('serviceAssignments.form.account'), assignment.accountName)}
            </div>
          ) : (
            <>
              <SegmentedField<ServiceAssignmentFormValues>
                name="scope"
                label={t('serviceAssignments.form.scope')}
                options={scopeOptions}
              />
              {lockedClient ? (
                readonlyRow(t('serviceAssignments.form.client'), lockedClient.name)
              ) : (
                <SearchableSelectField<ServiceAssignmentFormValues>
                  name="clientId"
                  label={t('serviceAssignments.form.client')}
                  required
                  placeholder={t('serviceAssignments.form.clientPlaceholder')}
                  options={clientOptions}
                />
              )}
              {scope === 'account' && (
                <SearchableSelectField<ServiceAssignmentFormValues>
                  name="accountId"
                  label={t('serviceAssignments.form.account')}
                  required
                  placeholder={
                    clientId === ''
                      ? t('serviceAssignments.form.accountPickClient')
                      : t('serviceAssignments.form.accountPlaceholder')
                  }
                  options={accountOptions}
                />
              )}
            </>
          )}

          {/* 2. Szolgáltatás → konstrukció (plan) kaszkád. Nem-számlázottnál VÁLTHATÓ (a BFF újraépíti a snapshotot +
            az árakat); SZÁMLÁZOTTNÁL zárolt (readonly). A gate a `pricingEditable` (= create VAGY nem-számlázott edit). */}
          {!pricingEditable ? (
            <div className="space-y-3">
              {readonlyRow(t('serviceAssignments.form.service'), assignment?.serviceName)}
              {readonlyRow(t('serviceAssignments.form.plan'), assignment?.planName)}
            </div>
          ) : (
            <>
              <SelectField<ServiceAssignmentFormValues>
                name="serviceId"
                label={t('serviceAssignments.form.service')}
                required
                placeholder={t('serviceAssignments.form.servicePlaceholder')}
                options={serviceOptions}
              />
              <SearchableSelectField<ServiceAssignmentFormValues>
                name="servicePlanId"
                label={t('serviceAssignments.form.plan')}
                required
                placeholder={
                  serviceId === ''
                    ? t('serviceAssignments.form.planPickService')
                    : t('serviceAssignments.form.planPlaceholder')
                }
                options={planOptions}
              />
              {selectedPlan && (
                <div className="border-border text-muted-foreground rounded-lg border p-3 text-xs">
                  {selectedPlan.billingType && (
                    <div>
                      {t('serviceAssignments.form.planBillingType')}:{' '}
                      {t(`serviceAssignments.billingTypeLabel.${selectedPlan.billingType}`, {
                        defaultValue: selectedPlan.billingType,
                      })}
                    </div>
                  )}
                  {selectedPlan.pricingType && (
                    <div>
                      {t('serviceAssignments.form.planPricingType')}:{' '}
                      {t(`serviceAssignments.pricingTypeLabel.${selectedPlan.pricingType}`, {
                        defaultValue: selectedPlan.pricingType,
                      })}
                    </div>
                  )}
                  {/* ÁFA-KULCS — a katalógus értéke, ÉS (ha eltér) a soron ténylegesen érvényes.
                      MIÉRT: a doboz a KONSTRUKCIÓ adatait írja, a számlára viszont a sor SAJÁT kulcsa megy
                      (a generátor a `planSnapshot.vatRate`-et olvassa). Eddig a feltűnőbb, összegző helyen
                      a katalógus 27%-a állt akkor is, amikor a kolléga lent fordított adózást választott —
                      a két érték közül a NEM érvényes volt a szembetűnő (user-lelet). Élesben 31 soron tér
                      el a felülírás a katalógustól. */}
                  {selectedPlan.vatRate && (
                    <div>
                      {t('serviceAssignments.form.planVatRate')}:{' '}
                      {t(`serviceAssignments.vatRateLabel.${selectedPlan.vatRate}`, {
                        defaultValue: selectedPlan.vatRate,
                      })}
                      {vatRate && vatRate !== selectedPlan.vatRate && (
                        <span className="text-warning font-medium">
                          {' → '}
                          {t('serviceAssignments.form.vatRateOverridden', {
                            vatRate: t(`serviceAssignments.vatRateLabel.${vatRate}`, {
                              defaultValue: vatRate,
                            }),
                          })}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* 3. Típus + státusz + mennyiség. A típus szerkesztéskor is váltható — számlázott soron is (elírás
            javítására; a JÖVŐBELI generálásra hat, a kiállított számlákat nem érinti). Az ESETI díj mindig
            nyilvántartásos (a plan-váltás is erre kényszerít) → ott csak az ÉLŐ `type` értéket mutatjuk. */}
          {isEdit && isOneTime ? (
            readonlyRow(
              t('serviceAssignments.form.type'),
              t(`serviceAssignments.typeLabel.${type}`, { defaultValue: type }),
              t('serviceAssignments.form.oneTimeTypeHint'),
            )
          ) : (
            <SegmentedField<ServiceAssignmentFormValues>
              name="type"
              label={t('serviceAssignments.form.type')}
              options={typeOptions}
            />
          )}
          <SelectField<ServiceAssignmentFormValues>
            name="status"
            label={t('serviceAssignments.form.status')}
            required
            options={statusOptions}
            disabled={dis('status')}
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField<ServiceAssignmentFormValues>
              name="quantity"
              label={t('serviceAssignments.form.quantity')}
              required
              disabled={dis('quantity')}
            />
            <SelectField<ServiceAssignmentFormValues>
              name="quantityUnit"
              label={t('serviceAssignments.form.quantityUnit')}
              options={unitOptions}
              disabled={dis('quantityUnit')}
            />
          </div>

          {/* 3b. Használat-forrás — CSAK tiered/base_tiered/commission árazásnál (kötelező). A tényleges usage-adat-
            feed NEM ehhez a körhöz tartozik; itt csak a form-mező + a választás + a validáció él. Számlázottnál zárolt. */}
          {showUsageSource && (
            <SelectField<ServiceAssignmentFormValues>
              name="usageSourceType"
              label={t('serviceAssignments.form.usageSourceType')}
              required
              options={usageSourceOptions}
              hint={t('serviceAssignments.form.usageSourceHint')}
              disabled={dis('usageSourceType')}
            />
          )}

          {/* 3c. MÉRT AZONOSÍTÓK — használat-alapú tételnél kötelező. Számlázott soron is szerkeszthető: a kötés a
              jövőbeli számlákra hat, és pont a rossz kötés javítása a cél. */}
          {showUsageRefs && (
            <Controller<ServiceAssignmentFormValues, 'usageRefs'>
              name="usageRefs"
              control={control}
              render={({ field, fieldState }) => (
                <UsageRefsSelect
                  options={refOptions}
                  value={Array.isArray(field.value) ? field.value : []}
                  onChange={field.onChange}
                  showAccount={scope === 'client'}
                  error={fieldState.error?.message}
                />
              )}
            />
          )}

          {/* 3d. MEGOSZTOTT HASZNÁLAT — CSAK a regisztráltak utáni loyalty-nál (`rs_loyalty`): ha több hotel ugyanarra
              az azonosítóra köt, a regisztráltak utáni díj a bekapcsolt társak között oszlik. A kötés párja, ezért
              számlázott soron is szerkeszthető (a jövőbeli számlákra hat). */}
          {showUsageRefs && canSplitSharedUsage(usageSourceType) && (
            <SwitchField<ServiceAssignmentFormValues>
              name="splitSharedUsage"
              label={t('serviceAssignments.form.splitSharedUsage')}
              hint={t('serviceAssignments.form.splitSharedUsageHint')}
            />
          )}

          {/* 4. ÁR-OVERRIDE blokk — down-conversion korlát + ÁFA + a közös PricingEditor.
              ⚠️ A NYILVÁNTARTÁS (`tracking`) SORON IS LÁTSZIK. Ezt a blokkot korábban a `billing` típus
              kapuzta, mert a nyilvántartás-sor nem kapott ár-sort — a felhasználó viszont nem értette, miért
              „nem jelenik meg az ár" egy olyan soron, aminek az adatbázisban VAN ára (a legacy CRM-ből
              hozva). A döntés: a nyilvántartás-sort SOHA nem számlázzuk, de az ára rögzíthető és látható,
              mert a kimutatásban szerepelnie kell. Ami SZÁMLÁZÁS-specifikus (kibocsátó, bizonylat-típus,
              kézbesítés, gyakoriság, megállapodás, számlatétel-szövegek), az továbbra is billing-only. */}
          <div className="border-border space-y-4 rounded-lg border p-4">
            <p className="text-sm font-semibold">{t('serviceAssignments.form.sectionPricing')}</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SelectField<ServiceAssignmentFormValues>
                name="pricing.pricingType"
                label={t('serviceAssignments.form.pricingType')}
                required
                options={pricingTypeOptions}
                disabled={!pricingEditable || hasComponents}
                hint={t('serviceAssignments.form.pricingTypeHint')}
              />
              <SelectField<ServiceAssignmentFormValues>
                name="pricing.vatRate"
                label={t('serviceAssignments.form.vatRate')}
                required
                options={vatRateOptions}
                disabled={!pricingEditable}
              />
            </div>
            <PricingEditor<ServiceAssignmentFormValues>
              form={form}
              base="pricing"
              pricingType={pricingType}
              t={t}
              tPrefix="serviceAssignments.pricing"
              disabled={!pricingEditable || hasComponents}
            />
            {/* KOMPONENSEKKEL az ár a komponensek összege — itt nem írható át (egy beírt ár mentéskor csendben
                elveszne); a fő csomag alapdíja a komponens-listában, első sorként szerkeszthető. */}
            {hasComponents && (
              <p className="text-subtle text-xs">
                {t('serviceAssignments.form.pricingFromComponents', {
                  amount: componentsSummary.amount,
                  currency: componentsSummary.currency ?? '',
                })}
              </p>
            )}
          </div>

          {/* 4b. KOMPONENS-FA (komplex/csomag) — KÖZVETLENÜL az árazás alatt (user-döntés: a komponensek az ár forrásai,
            komponensekkel a fenti ár zárolt és az összegük). A gyerek-komponensek. A szülő ára a komponensek összege (fix díj).
            Szerkeszthető, ha nem-számlázott (`pricingEditable`); számlázottnál zárolt (a BFF is 422-zi). */}
          <div className="border-border space-y-3 rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">
                {t('serviceAssignments.form.sectionComponents')}
              </p>
              {pricingEditable && (
                <Button type="button" variant="outline" size="sm" onClick={addComponent}>
                  {t('serviceAssignments.form.addComponent')}
                </Button>
              )}
            </div>
            <p className="text-subtle text-xs">{t('serviceAssignments.form.componentsHint')}</p>
            {componentFields.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {t('serviceAssignments.form.noComponents')}
              </p>
            ) : (
              <div className="space-y-3">
                {componentFields.map((f, i) => {
                  const rowServiceId = componentsWatch?.[i]?.serviceId ?? '';
                  // A komponens-plan-lista is a FŐ plan billing-type-jára szűr (a legacy component-modal 1:1).
                  const rowPlanOptions = (refs?.plans ?? [])
                    .filter(
                      (p) =>
                        p.serviceId === rowServiceId && p.billingType === componentBillingTarget,
                    )
                    .map((p) => ({ value: p.id, label: p.name }));
                  return (
                    <div key={f.id} className="border-border/60 space-y-2 rounded-md border p-3">
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <SelectField<ServiceAssignmentFormValues>
                          name={`components.${i}.serviceId`}
                          label={t('serviceAssignments.form.componentService')}
                          options={componentServiceOptions}
                          disabled={!pricingEditable}
                        />
                        <SearchableSelectField<ServiceAssignmentFormValues>
                          name={`components.${i}.servicePlanId`}
                          label={t('serviceAssignments.form.componentPlan')}
                          options={rowPlanOptions}
                          disabled={!pricingEditable || rowServiceId === ''}
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <TextField<ServiceAssignmentFormValues>
                          name={`components.${i}.quantity`}
                          label={t('serviceAssignments.form.componentQuantity')}
                          disabled={!pricingEditable}
                        />
                        <TextField<ServiceAssignmentFormValues>
                          name={`components.${i}.unitPrice`}
                          label={t('serviceAssignments.form.componentUnitPrice')}
                          disabled={!pricingEditable}
                        />
                        <SelectField<ServiceAssignmentFormValues>
                          name={`components.${i}.currency`}
                          label={t('serviceAssignments.form.componentCurrency')}
                          options={currencyOptions}
                          disabled={!pricingEditable}
                        />
                      </div>
                      {/* KOMPONENS-SZINTŰ felelősök: a komplex szolgáltatás komponenseit külön kollégák
                          viszik (a legacy `formComponentContactIds[i]` 1:1 — a migrált adatban ez a többségi
                          eset). A pivot sosem zárolt, ezért számlázott hozzárendelésen is szerkeszthető. */}
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <ComboboxField<ServiceAssignmentFormValues>
                          name={`components.${i}.contactId`}
                          label={t('serviceAssignments.form.componentContact')}
                          placeholder={t('serviceAssignments.form.contactPlaceholder')}
                          search={searchIn(contactOptions)}
                          initialOption={optionById(
                            contactOptions,
                            componentsWatch?.[i]?.contactId ?? '',
                          )}
                        />
                        <ComboboxField<ServiceAssignmentFormValues>
                          name={`components.${i}.expertId`}
                          label={t('serviceAssignments.form.componentExpert')}
                          placeholder={t('serviceAssignments.form.expertPlaceholder')}
                          search={searchIn(expertOptions)}
                          initialOption={optionById(
                            expertOptions,
                            componentsWatch?.[i]?.expertId ?? '',
                          )}
                        />
                      </div>
                      {pricingEditable && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeComponent(i)}
                        >
                          {t('serviceAssignments.form.removeComponent')}
                        </Button>
                      )}
                    </div>
                  );
                })}
                <div className="text-sm font-medium">
                  {t('serviceAssignments.form.componentsTotal')}: {componentsSummary.amount}{' '}
                  {componentsSummary.currency ?? ''}
                </div>
                {componentsSummary.currency === null && (
                  <p className="text-destructive text-xs">
                    {t('serviceAssignments.form.componentsCurrencyMismatch')}
                  </p>
                )}
              </div>
            )}
            {isEdit && detail?.invoiced && (
              <p className="text-warning text-xs">
                {t('serviceAssignments.form.componentsLocked')}
              </p>
            )}
          </div>

          {/* 5. BILLING-blokk (feltételes) — kibocsátó, bizonylat, gyakoriság, megállapodás, dátumok, kártya, számlatétel. */}
          {isBilling && (
            <div className="border-border space-y-4 rounded-lg border p-4">
              <p className="text-sm font-semibold">{t('serviceAssignments.form.sectionBilling')}</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <SelectField<ServiceAssignmentFormValues>
                  name="issuerKey"
                  label={t('serviceAssignments.form.issuer')}
                  options={issuerOptions}
                  disabled={dis('issuerKey')}
                />
                <SelectField<ServiceAssignmentFormValues>
                  name="invoiceType"
                  label={t('serviceAssignments.form.invoiceType')}
                  options={invoiceTypeOptions}
                  disabled={dis('invoiceType')}
                />
                {/* DÍJBEKÉRŐ csak papír alapú lehet (nincs e-díjbekérő) — a választó ilyenkor eltűnik, az érték
                    papír alapúra áll (l. a lenti effektet; a body-építő és a BFF is erre kényszerít). */}
                {invoiceType !== 'proforma' && (
                  <SelectField<ServiceAssignmentFormValues>
                    name="invoiceDeliveryType"
                    label={t('serviceAssignments.form.invoiceDeliveryType')}
                    options={deliveryOptions}
                    disabled={dis('invoiceDeliveryType')}
                  />
                )}
                <SelectField<ServiceAssignmentFormValues>
                  name="billingFrequency"
                  label={t('serviceAssignments.form.billingFrequency')}
                  options={frequencyOptions}
                  disabled={dis('billingFrequency')}
                />
                <SelectField<ServiceAssignmentFormValues>
                  name="billingAgreement"
                  label={t('serviceAssignments.form.billingAgreement')}
                  options={agreementOptions}
                  disabled={dis('billingAgreement')}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DateField<ServiceAssignmentFormValues>
                  name="billingStartDate"
                  label={t('serviceAssignments.form.billingStartDate')}
                  required={!isEdit}
                  disabled={dis('billingStartDate')}
                />
                <DateField<ServiceAssignmentFormValues>
                  name="billingEndDate"
                  label={t('serviceAssignments.form.billingEndDate')}
                  disabled={dis('billingEndDate')}
                />
              </div>
              <TextField<ServiceAssignmentFormValues>
                name="invoiceItemName"
                label={t('serviceAssignments.form.invoiceItemName')}
                hint={t('serviceAssignments.form.invoiceItemNameHint')}
                disabled={dis('invoiceItemName')}
              />
              <TextareaField<ServiceAssignmentFormValues>
                name="invoiceItemNote"
                label={t('serviceAssignments.form.invoiceItemNote')}
                rows={2}
                disabled={dis('invoiceItemNote')}
              />
              <SwitchField<ServiceAssignmentFormValues>
                name="firstInvoiceProrated"
                label={t('serviceAssignments.form.firstInvoiceProrated')}
                hint={t('serviceAssignments.form.firstInvoiceProratedHint')}
                disabled={dis('firstInvoiceProrated')}
              />
              <SwitchField<ServiceAssignmentFormValues>
                name="showComponentsOnInvoice"
                label={t('serviceAssignments.form.showComponentsOnInvoice')}
                hint={t('serviceAssignments.form.showComponentsOnInvoiceHint')}
                disabled={dis('showComponentsOnInvoice')}
              />
            </div>
          )}

          {/* 6. TRACKING-blokk (feltételes) — szerződés dátumai + a tétel megnevezése. A számlatétel neve és
              megjegyzése itt is látszik: az ESETI díjnál (pl. setup díj) ez a pontos megnevezés — a katalógus-név
              általános („Digital marketing setup díj"), a konkrét munka („Cookiebot szoftver beüzemelése …") a
              megjegyzésben áll. Korábban CSAK számlázás-soron volt látható, így eseti soron sehol (user-lelet). */}
          {!isBilling && (
            <div className="space-y-4">
              {/* ESETI DÍJ: EGYETLEN dátum — a „Számlázás dátuma" (a számlázási kezdet ÉS vég; a kimutatás ennek a
                  hónapjába sorolja). A szerződés-dátumok a jegyzet fölött állnak — eseti díjnál nincsenek. */}
              {isOneTime && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <DateField<ServiceAssignmentFormValues>
                    name="billingStartDate"
                    label={t('serviceAssignments.form.billingDate')}
                    hint={t('serviceAssignments.form.billingDateHint')}
                    required
                    disabled={dis('billingStartDate')}
                  />
                </div>
              )}
              <TextField<ServiceAssignmentFormValues>
                name="invoiceItemName"
                label={t('serviceAssignments.form.invoiceItemName')}
                hint={t('serviceAssignments.form.invoiceItemNameHint')}
                disabled={dis('invoiceItemName')}
              />
              <TextareaField<ServiceAssignmentFormValues>
                name="invoiceItemNote"
                label={t('serviceAssignments.form.invoiceItemNote')}
                rows={2}
                disabled={dis('invoiceItemNote')}
              />
            </div>
          )}

          {/* 7. Felelősök (kapcsolattartó / szakértő) — pivot; sosem zárolt.
              KERESŐS választó (`ComboboxField` → `EntityCombobox`), a support-csapatok tag-választójának
              vizuális mintája szerint: a személy-lista több száz elemű is lehet, egy sima `select`-ben
              görgetni kell hozzá. A két mező KÜLÖN halmazból választ (külsős / belsős — lásd fent). */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ComboboxField<ServiceAssignmentFormValues>
              name="contactId"
              label={t('serviceAssignments.form.contact')}
              placeholder={t('serviceAssignments.form.contactPlaceholder')}
              search={searchIn(contactOptions)}
              initialOption={optionById(contactOptions, watch('contactId'))}
            />
            <ComboboxField<ServiceAssignmentFormValues>
              name="expertId"
              label={t('serviceAssignments.form.expert')}
              placeholder={t('serviceAssignments.form.expertPlaceholder')}
              search={searchIn(expertOptions)}
              initialOption={optionById(expertOptions, watch('expertId'))}
            />
            {/* FIZETŐKÁRTYA — user-döntés: a helye a FELELŐSÖK mellett van, nem a számlázási blokkban.
                A mező nem számlázási paraméter: azt mondja meg, MELYIK ügyfél-kártya fizeti ezt a
                szolgáltatást, és ebből derül ki, KINEK kell szólni a kártya lejáratakor (a lejárat-értesítő
                ezen a kapcsolaton át oldja fel a címzetteket — a hozzárendelés kapcsolattartója/szakértője,
                fallbackben a fiók/ügyfél kapcsolattartói). Ugyanaz a kérdés tehát, mint a másik két mezőé:
                ki a felelős. */}
            <SelectField<ServiceAssignmentFormValues>
              name="paymentCardId"
              label={t('serviceAssignments.form.paymentCard')}
              options={paymentCardOptions}
              disabled={dis('paymentCardId')}
            />
          </div>

          {/* 8. Copy-to-accounts (CSAK client-scope) — érintett fiókok; pivot, sosem zárolt. */}
          {scope === 'client' && (
            <Controller<ServiceAssignmentFormValues, 'accountIds'>
              name="accountIds"
              control={control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t('serviceAssignments.form.accountIds')}
                  </label>
                  <EntityMultiSelect
                    options={accountMultiOptions}
                    value={Array.isArray(field.value) ? field.value : []}
                    onChange={field.onChange}
                    placeholder={
                      clientId === ''
                        ? t('serviceAssignments.form.accountPickClient')
                        : t('serviceAssignments.form.accountIdsPlaceholder')
                    }
                    countLabel={(count) => t('serviceAssignments.form.accountIdsCount', { count })}
                    aria-label={t('serviceAssignments.form.accountIds')}
                  />
                  <p className="text-subtle text-xs">
                    {t('serviceAssignments.form.accountIdsHint')}
                  </p>
                  {fieldState.error?.message && (
                    <p className="text-destructive text-sm">{fieldState.error.message}</p>
                  )}
                </div>
              )}
            />
          )}

          {/* SZERZŐDÉS DÁTUMAI — minden típusnál a jegyzet fölött (user-döntés), az ESETI díj kivételével (annak
              egyetlen dátuma a „Számlázás dátuma"). Tájékoztató adat: a számlázást a számlázási pár vezérli. */}
          {!isOneTime && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DateField<ServiceAssignmentFormValues>
                name="contractStartDate"
                label={t('serviceAssignments.form.contractStartDate')}
                disabled={dis('contractStartDate')}
              />
              <DateField<ServiceAssignmentFormValues>
                name="contractEndDate"
                label={t('serviceAssignments.form.contractEndDate')}
                disabled={dis('contractEndDate')}
              />
            </div>
          )}

          <TextareaField<ServiceAssignmentFormValues>
            name="note"
            label={t('serviceAssignments.form.note')}
            rows={3}
            disabled={dis('note')}
          />
        </Form>
      </FormSheet>

      {/* Aktív-hozzárendelés FIGYELMEZTETÉS (nem-blokkoló) — mentés ELŐTT, ha már van aktív hozzárendelés. */}
      <ServiceAssignmentActiveWarningDialog
        open={warnOpen}
        onOpenChange={(o) => {
          if (!o) {
            setWarnOpen(false);
            setPendingValues(null);
          }
        }}
        existing={warnExisting}
        pending={mutation.isPending}
        onProceed={() => {
          setWarnOpen(false);
          if (pendingValues) doSubmit(pendingValues);
        }}
        onViewExisting={() => {
          // Nincs per-hozzárendelés detail-route → visszatérünk a listához (a meglévő ott megtalálható).
          setWarnOpen(false);
          onOpenChange(false);
        }}
      />
    </>
  );
}
